import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core'

import { FlxNotificationComponent } from './notification/notification.component'
import { NotificationsCenterStore } from './notifications-center/notifications-center.store'
import { NOTIFICATIONS_DATA } from './notifications.model'

@Component({
  selector: 'flx-notifications',
  imports: [FlxNotificationComponent],
  templateUrl: './notifications.component.html',
  styleUrl: './notifications.component.scss',
  host: {
    '(mouseenter)': 'setHovered(true)',
    '(mouseleave)': 'setHovered(false)',
    '(focusin)': 'setFocused(true)',
    '(focusout)': 'onFocusOut($event)',
    '(document:visibilitychange)': 'syncPaused()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxNotificationsComponent {
  #document = inject(DOCUMENT)
  #notificationsCenterStore = inject(NotificationsCenterStore)
  #hovered = false
  #focused = false

  data = inject(NOTIFICATIONS_DATA)

  paused = signal(false)

  constructor() {
    // visibilitychange only fires on a change, so a stack created while the tab is
    // already hidden has to seed the pause itself or its cards would tick down unseen.
    this.syncPaused()

    // Clear pause state when there are no active notifications, to avoid the stack from
    // freezing because no events are emitted when dismissing the last notification.
    effect(() => {
      if (!this.data.notifications().length) {
        this.#hovered = false
        this.#focused = false
        untracked(() => this.syncPaused())
      }
    })
  }

  markAsRead(notificationId: string): void {
    void this.#notificationsCenterStore.markAsRead(notificationId)
  }

  setHovered(hovered: boolean): void {
    this.#hovered = hovered
    this.syncPaused()
  }

  setFocused(focused: boolean): void {
    this.#focused = focused
    this.syncPaused()
  }

  // focusout also fires when focus moves between two cards' controls, which would resume the
  // stack mid-tab and let a queued card slide in. Only leaving the region counts.
  onFocusOut(event: FocusEvent): void {
    const region = event.currentTarget as HTMLElement

    if (!region.contains(event.relatedTarget as Node)) {
      this.setFocused(false)
    }
  }

  syncPaused(): void {
    const paused = this.#hovered || this.#focused || this.#document.hidden
    this.paused.set(paused)
    this.data.setPaused(paused)
  }
}
